import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ShoppingCart, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { shopApi } from "@/lib/api";
import { useCart } from "@/lib/store";

/** Post-add-to-cart popup with 3-4 co-purchased items.
 *  Auto-dismisses if the store returns no related items so it never blocks the shopper.
 */
export default function UpsellModal() {
  const { upsellFor, setUpsellFor, setOpen, add } = useCart();
  const productId = upsellFor?.product_id;

  const { data: related = [], isLoading } = useQuery({
    queryKey: ["related", productId],
    queryFn: () => shopApi.related(productId, 4),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  });

  const close = () => setUpsellFor(null);
  const openCart = () => { close(); setOpen(true); };

  // No related items → just open the cart drawer directly (no popup shown)
  React.useEffect(() => {
    if (!productId) return;
    if (!isLoading && related.length === 0) openCart();
    // eslint-disable-next-line
  }, [isLoading, related.length, productId]);

  if (!upsellFor || related.length === 0) return null;

  const addAndKeep = (p) => {
    // openDrawer=false so it doesn't recursively re-trigger this popup for the upsell item.
    add(p, 1, null, null, false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-card border-white/10 max-w-2xl" data-testid="upsell-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary text-sm mb-1">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20">
              <Check className="h-3.5 w-3.5" />
            </span>
            Added to your cart
          </div>
          <DialogTitle className="text-xl leading-tight">Customers who bought this also love these</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">Add anything you like — one tap, no re-checkout.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {related.slice(0, 4).map((p) => (
            <div key={p.product_id} className="rounded-xl border border-white/10 bg-secondary/30 p-3 flex gap-3 items-center" data-testid={`upsell-item-${p.product_id}`}>
              <img src={p.images?.[0]} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground uppercase tracking-wider truncate">{p.brand}</p>
                <p className="text-sm font-medium leading-tight line-clamp-2">{p.title}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-sm font-bold text-primary">${p.price?.toFixed(2)}</span>
                  {p.compare_at_price > p.price && (
                    <span className="text-[11px] line-through text-muted-foreground">${p.compare_at_price?.toFixed(2)}</span>
                  )}
                </div>
              </div>
              <Button size="sm" variant="outline" className="border-primary/40 text-primary hover:bg-primary hover:text-black shrink-0" onClick={() => addAndKeep(p)} data-testid={`upsell-add-${p.product_id}`}>
                Add
              </Button>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/5">
          <button onClick={close} className="text-xs text-muted-foreground hover:text-white flex items-center gap-1">
            <X className="h-3 w-3" /> No thanks
          </button>
          <Button className="btn-primary" onClick={openCart} data-testid="upsell-view-cart">
            <ShoppingCart className="h-4 w-4 mr-2" /> View cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
